import { createFileRoute, redirect, useSearch } from "@tanstack/react-router";

import { graphql } from "@repo/graphql-types/gql";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import { CpsAlert } from "corpus";

import { ContactsHistoryDownloadButton } from "@/components/assist/assist-contacts-history/contacts-history-download-button";
import { ContactsHistoryFilterContainer } from "@/components/assist/assist-contacts-history/contacts-history-filter-container";
import { ContactsHistoryHeader } from "@/components/assist/assist-contacts-history/contacts-history-header";
import { ContactsHistoryList } from "@/components/assist/assist-contacts-history/contacts-history-list";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import {
  HeaderButton,
  HeaderLinkButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { AssistFeatures } from "@/enums/assist-features";
import { resolveContactsHistoryDates } from "@/hooks/use-contacts-history-filter";
import { useGraphQL } from "@/hooks/use-graphql";
import { validateFeatureAccess } from "@/lib/assist-feature-guard";
import { isProUserWithActiveTrial } from "@/lib/assist-guard";
import { FIVE_MINUTES_STALE_TIME } from "@/lib/constants/dashboards-customer-overview";
import { AssistBillingTypes } from "@/lib/enums/assist-billing-types";
import { formatDateAndTimeIgnoringTimezone } from "@/lib/helpers/date";

import type { JSX } from "react";

const ContactsHistoryPageQuery = graphql(/* GraphQL */ `
  query ContactsHistoryPageQuery($ids: [Int!]!) {
    nuntius_lote_contatos_cobranca(
      where: { id: { _in: $ids } },
      order_by: { contatosPorDia: { data: desc } }
    ) {
      ...ContactsHistoryListFragment
    }
  }
`);

const ContactsHistoryDownloadButtonQuery = graphql(/* GraphQL */ `
  query ContactsHistoryDownloadButtonQuery($ids: [Int!]!) {
    NuntiusGetAssistContactBatchDetailsBulk(arg1: { contactBatchIds: $ids }) {
      ...ContactsHistoryDownloadButtonFragment
    }
  }
`);

const ContactsHistoryHeaderBillingQuery = graphql(/* GraphQL */ `
  query ContactsHistoryHeaderBillingQuery($from: date, $to: date, $assistProContactsIdentification: String!) {
    billing: tbMovimentacoes(
      where: {
        _and: [
          { dataVencimento: { _gte: $from, _lte: $to } }
          { TipoMovimentacao: { identificacao: { _eq: $assistProContactsIdentification } } }
        ]
      }
    ) {
      codOrigem
      ...ContactsHistoryHeaderBillingFragment
    }
  }
`);

const ContactsHistoryPage = (): JSX.Element => {
  const searchParams = useSearch({
    from: "/settings/assist/contacts-history/",
  });

  const { from, to } = resolveContactsHistoryDates(searchParams, 15);
  const fromDate = formatDateAndTimeIgnoringTimezone(from, "00:00", "yyyy-MM-dd");
  const toDate = formatDateAndTimeIgnoringTimezone(to, "00:00", "yyyy-MM-dd");

  const billingQueryResult = useGraphQL(
    ContactsHistoryHeaderBillingQuery,
    {
      from: fromDate,
      to: toDate,
      assistProContactsIdentification: AssistBillingTypes.ContatosAssistPro,
    },
    {
      staleTime: FIVE_MINUTES_STALE_TIME,
    },
  );

  const { data: billingData } = billingQueryResult;

  const contactIds =
    billingData?.billing
      .map((item) => item.codOrigem)
      .filter((id): id is number => typeof id === "number") ?? [];

  const listQueryResult = useGraphQL(
    ContactsHistoryPageQuery,
    {
      ids: contactIds,
    },
    {
      staleTime: FIVE_MINUTES_STALE_TIME,
    },
  );

  const { data: listData } = listQueryResult;

  const downloadButtonQueryResult = useGraphQL(
    ContactsHistoryDownloadButtonQuery,
    {
      ids: contactIds,
    },
    {
      enabled: contactIds.length > 0,
      staleTime: FIVE_MINUTES_STALE_TIME,
    },
  );
  const { data: downloadButtonData } = downloadButtonQueryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Raio-X de Contatos" />
        <HeaderLinkButton
          icon={HelpCircle}
          align="end"
          aria-label="Sobre"
          to="/settings/assist/contacts-history/about"
        />
      </HeaderRoot>

      <Page>
        <AsyncDataWrapper {...billingQueryResult}>
          {billingData && listData ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-6">
                <ContactsHistoryHeader billingData={billingData.billing} />

                <div className="flex flex-col gap-2">
                  <ContactsHistoryFilterContainer />
                  <ContactsHistoryDownloadButton
                    data={
                      downloadButtonData?.NuntiusGetAssistContactBatchDetailsBulk ??
                      undefined
                    }
                    fromDate={fromDate}
                    toDate={toDate}
                  />
                </div>
              </div>

              <CpsAlert
                type="info"
                title="Importante"
                description="Os contatos podem levar até 48h para aparecer aqui. O valor mostrado corresponde aos contatos atendidos no período selecionado."
              />

              {listData.nuntius_lote_contatos_cobranca.length ? (
                <ContactsHistoryList data={listData.nuntius_lote_contatos_cobranca} />
              ) : (
                <EmptyStateRoot id="contacts-history-empty-state">
                  <EmptyStateIcon icon={ExclamationCircle} />
                  <EmptyStateTitle id="contacts-history-empty-state-title">
                    Nenhum contato atendido
                  </EmptyStateTitle>
                  <EmptyStateDescription id="contacts-history-empty-state-description">
                    Os dados podem levar até 48h para aparecer. Se preferir, ajuste o
                    período de busca e tente novamente.
                  </EmptyStateDescription>
                </EmptyStateRoot>
              )}
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/contacts-history/")({
  component: ContactsHistoryPage,
  beforeLoad: ({ context, location }) => {
    const { user, flags } = context;

    const showContactHistoryTrialItem =
      flags["habilita-historico-de-contatos-no-menu-do-assist-trial"];

    if (isProUserWithActiveTrial(user) && showContactHistoryTrialItem) {
      redirect({
        to: "/settings/assist/contacts-history/trial-period",
        throw: true,
      });
    }

    validateFeatureAccess({
      location,
      user,
      codFuncionalidade: AssistFeatures.ContactsHistory,
    });
  },
});
